<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import { almConfig } from "@/almConfig";
import {
  almConnectionsWidgetAddClicked,
  almConnectionsWidgetConnectionOpened,
  almConnectionsWidgetViewAllClicked,
} from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import { AlmConfigurationHandler } from "@/handlers/almConfigurationHandler";
import { createHandlerContext } from "@/stores/context";

import BaseButton from "../BaseButton.vue";
import BaseSpinner from "../BaseSpinner.vue";
import SvgIcon from "../SvgIcon.vue";
import KebabMenu from "./KebabMenu.vue";
import WidgetCard from "./WidgetCard.vue";

const MAX_VISIBLE = 5;

const router = useRouter();

const connections = ref([]);
const isLoading = ref(true);
const loadError = ref(false);

async function load() {
  isLoading.value = true;
  loadError.value = false;
  try {
    const data = await new AlmConfigurationHandler(createHandlerContext()).getAlmConnections();
    connections.value = data || [];
  } catch (e) {
    loadError.value = true;
    throw e;
  } finally {
    isLoading.value = false;
  }
}

onMounted(load);

const visibleConnections = computed(() => connections.value.slice(0, MAX_VISIBLE));

const allSameType = computed(() => {
  if (!connections.value.length) return false;
  const first = connections.value[0].alm_type;
  return connections.value.every((c) => c.alm_type === first);
});

const typeLabel = (type) => almConfig(type).displayName;

const stateOf = (connection) => {
  if (connection.state === "error") return { label: "Error", tone: "error" };
  if (connection.state === "okay") return { label: "Connected", tone: "okay" };
  return { label: "Unknown", tone: "neutral" };
};

const displayName = (connection) => connection.name?.trim() || "(unnamed)";

function editConnection(connection) {
  trackEvent(almConnectionsWidgetConnectionOpened());
  router.push({
    name: "editAlmConnection",
    params: { connectionId: connection.id, almType: connection.alm_type },
  });
}

function viewEvents(connection) {
  router.push({
    name: "almConnectionEvents",
    params: { connectionId: connection.id, almType: connection.alm_type },
  });
}

function editTeamMappings(connection) {
  router.push({
    name: "editAlmTeamMapping",
    params: { connectionId: connection.id, almType: connection.alm_type },
  });
}

function goAll() {
  trackEvent(almConnectionsWidgetViewAllClicked());
  router.push({ name: "almConnections" });
}

function addConnection() {
  trackEvent(almConnectionsWidgetAddClicked());
  router.push({ name: "AddAlmConnection" });
}
</script>

<template>
  <WidgetCard
    class="alm-connections-widget"
    icon="general/alm-connection"
    :title-to="{ name: 'almConnections' }"
  >
    <template #title>ALM connections</template>
    <template #actions>
      <BaseButton
        variant="outlined"
        color="grey"
        size="small"
        icon-before="system/plus"
        aria-label="Add ALM connection"
        @click="addConnection"
      />
    </template>

    <ul v-if="visibleConnections.length" class="list">
      <li v-for="connection in visibleConnections" :key="connection.id" class="row-item">
        <button type="button" class="row" @click="editConnection(connection)">
          <span class="status-dot" :class="stateOf(connection).tone" aria-hidden="true" />
          <span class="name">{{ displayName(connection) }}</span>
          <span v-if="stateOf(connection).tone === 'error'" class="status-chip error"> Error </span>
          <span v-if="!allSameType" class="type">
            {{ typeLabel(connection.alm_type) }}
          </span>
        </button>
        <KebabMenu :label="`Actions for ${displayName(connection)}`">
          <!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
          <li tabindex="-1" role="menuitem" class="list-item" @click="editConnection(connection)">
            Edit connection
          </li>
          <li tabindex="-1" role="menuitem" class="list-item" @click="editTeamMappings(connection)">
            Team mappings
          </li>
          <li tabindex="-1" role="menuitem" class="list-item" @click="viewEvents(connection)">
            View events
          </li>
          <!-- eslint-enable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
        </KebabMenu>
      </li>
    </ul>

    <div v-else-if="isLoading" class="loading">
      <BaseSpinner delayed size="20" />
    </div>

    <div v-else-if="loadError" class="empty">
      <p class="empty-copy">Something went wrong while loading ALM connections.</p>
    </div>

    <div v-else class="empty">
      <p class="empty-copy">No ALM connections yet. They'll show up here once you add one.</p>
    </div>

    <template v-if="connections.length" #footer>
      <button type="button" class="view-all-link" @click="goAll">
        View all connections
        <SvgIcon name="system/chevron-down" width="14" height="14" class="view-all-chevron" />
      </button>
      <span class="footer-meta">{{ connections.length }} total</span>
    </template>
  </WidgetCard>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/components-ng/home/widget-list";
@use "@/styles/mixins/typography";
@include widget-list.chrome;

.list {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  gap: 2px 10px;
  align-content: start;
}

.row-item,
.row {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  align-items: center;
}

.row {
  padding-right: 58px;
}

.status-dot {
  grid-column: 1;
}

.name {
  grid-column: 2;
}

.status-chip {
  grid-column: 3;
}

.type {
  grid-column: 4;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: c.$grey-light;
  flex-shrink: 0;

  &.okay {
    background: c.$green;
  }

  &.error {
    background: c.$red;
  }
}

.status-chip {
  @include typography.caption("semi-bold");

  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;

  &.error {
    background: c.$red-light;
    color: c.$red;
  }
}

.type {
  @include typography.label("medium");

  color: c.$text-subtle;
  white-space: nowrap;
  text-align: left;
}
</style>
